import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

interface ClientBusinessTabProps {
  clientId: string;
  clientName: string;
}

export function ClientBusinessTab({ clientId, clientName }: ClientBusinessTabProps) {

  // Fetch invoices for this client
  const { data: invoices, isLoading } = useQuery({
    queryKey: ["client-invoices-chart", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id, title, amount, issue_date, created_at")
        .eq("client_id", clientId)
        .not("amount", "is", null)
        .order("issue_date", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!clientId,
  });

  // Process data for the chart
  const chartData = useMemo(() => {
    if (!invoices) return [];

    // Create data structure for each month from 2023-2025
    const months: { [key: string]: { month: string; "2023": number; "2024": number; "2025": number; invoices: { [year: string]: string[] } } } = {};
    
    // Initialize all months for 3 years
    const years = [2023, 2024, 2025];
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    monthNames.forEach(month => {
      months[month] = {
        month,
        "2023": 0,
        "2024": 0,
        "2025": 0,
        invoices: {
          "2023": [],
          "2024": [],
          "2025": []
        }
      };
    });

    // Aggregate invoice values by month and year
    invoices.forEach(invoice => {
      const date = new Date(invoice.issue_date || invoice.created_at);
      const year = date.getFullYear();
      const month = monthNames[date.getMonth()];

      if (year >= 2023 && year <= 2025 && months[month]) {
        months[month][year.toString() as "2023" | "2024" | "2025"] += Number(invoice.amount) || 0;
        months[month].invoices[year.toString()].push(invoice.title || `Invoice ${invoice.id.substring(0, 8)}`);
      }
    });

    return monthNames.map(month => months[month]);
  }, [invoices]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium">{`${label}`}</p>
              {payload.map((entry: any, index: number) => (
            <div key={index} className="space-y-1">
              <p style={{ color: entry.color }}>
                {`${entry.dataKey}: €${entry.value.toLocaleString()}`}
              </p>
              {entry.payload.invoices[entry.dataKey]?.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <p className="font-medium">Invoices:</p>
                  {entry.payload.invoices[entry.dataKey].map((invoiceName: string, i: number) => (
                    <p key={i}>• {invoiceName}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const totalValue = invoices?.reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0) || 0;

  // Calculate yearly totals and trends
  const yearlyStats = useMemo(() => {
    if (!invoices) return { 2023: 0, 2024: 0, 2025: 0 };

    const stats = { 2023: 0, 2024: 0, 2025: 0 };
    
    invoices.forEach(invoice => {
      const date = new Date(invoice.issue_date || invoice.created_at);
      const year = date.getFullYear();
      
      if (year >= 2023 && year <= 2025) {
        stats[year as keyof typeof stats] += Number(invoice.amount) || 0;
      }
    });
    
    return stats;
  }, [invoices]);

  // Calculate YoY trends
  const trends = useMemo(() => {
    return {
      2024: yearlyStats[2023] > 0 ? ((yearlyStats[2024] - yearlyStats[2023]) / yearlyStats[2023] * 100) : 0,
      2025: yearlyStats[2024] > 0 ? ((yearlyStats[2025] - yearlyStats[2024]) / yearlyStats[2024] * 100) : 0
    };
  }, [yearlyStats]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="space-y-1">
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Business Overview - {clientName}</span>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Total Invoice Value: €{totalValue.toLocaleString()}
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* 2023 Summary */}
          <Card className="border-2" style={{ borderColor: '#22c55e' }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">2023 Total</p>
                  <p className="text-2xl font-bold" style={{ color: '#22c55e' }}>
                    €{yearlyStats[2023].toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                  <span className="text-lg font-bold" style={{ color: '#22c55e' }}>23</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2024 Summary */}
          <Card className="border-2" style={{ borderColor: '#3b82f6' }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">2024 Total</p>
                  <p className="text-2xl font-bold" style={{ color: '#3b82f6' }}>
                    €{yearlyStats[2024].toLocaleString()}
                  </p>
                  {yearlyStats[2023] > 0 && (
                    <div className="flex items-center mt-1">
                      {trends[2024] > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                      ) : trends[2024] < 0 ? (
                        <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                      ) : (
                        <Minus className="h-3 w-3 text-gray-500 mr-1" />
                      )}
                      <span className={`text-xs ${trends[2024] > 0 ? 'text-green-500' : trends[2024] < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                        {trends[2024] > 0 ? '+' : ''}{trends[2024].toFixed(1)}% vs 2023
                      </span>
                    </div>
                  )}
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#3b82f620' }}>
                  <span className="text-lg font-bold" style={{ color: '#3b82f6' }}>24</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2025 Summary */}
          <Card className="border-2" style={{ borderColor: '#ef4444' }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">2025 Total</p>
                  <p className="text-2xl font-bold" style={{ color: '#ef4444' }}>
                    €{yearlyStats[2025].toLocaleString()}
                  </p>
                  {yearlyStats[2024] > 0 && (
                    <div className="flex items-center mt-1">
                      {trends[2025] > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                      ) : trends[2025] < 0 ? (
                        <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                      ) : (
                        <Minus className="h-3 w-3 text-gray-500 mr-1" />
                      )}
                      <span className={`text-xs ${trends[2025] > 0 ? 'text-green-500' : trends[2025] < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                        {trends[2025] > 0 ? '+' : ''}{trends[2025].toFixed(1)}% vs 2024
                      </span>
                    </div>
                  )}
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#ef444420' }}>
                  <span className="text-lg font-bold" style={{ color: '#ef4444' }}>25</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {invoices && invoices.length > 0 ? (
          <div className="space-y-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis 
                    dataKey="month" 
                    className="text-xs"
                  />
                  <YAxis 
                    tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                    className="text-xs"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="2023" 
                    stroke="#22c55e" 
                    strokeWidth={2}
                    dot={{ fill: "#22c55e", strokeWidth: 2, r: 4 }}
                    name="2023"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="2024" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                    name="2024"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="2025" 
                    stroke="#ef4444" 
                    strokeWidth={2}
                    dot={{ fill: "#ef4444", strokeWidth: 2, r: 4 }}
                    name="2025"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            {/* Invoice Summary */}
            <div className="mt-6">
              <h4 className="font-medium mb-3">Recent Invoices</h4>
              <div className="space-y-2">
                {invoices.slice(0, 5).map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                    <span className="text-sm">{invoice.title || `Invoice ${invoice.id.substring(0, 8)}`}</span>
                    <span className="text-sm font-medium">€{Number(invoice.amount).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No invoices found</h3>
            <p className="text-muted-foreground mb-4">
              No invoices have been created for this client yet.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}